import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './LandingPage.css';

const LandingPage = () => {
  const canvasRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Black hole animation variables
    let time = 0;
    const particles = [];
    const numParticles = 200;
    
    // Create particles
    for (let i = 0; i < numParticles; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 2,
        vy: (Math.random() - 0.5) * 2,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.8 + 0.2,
        originalSize: Math.random() * 3 + 1,
        angle: Math.random() * Math.PI * 2,
        distance: Math.random() * 300 + 50,
        speed: Math.random() * 0.02 + 0.01
      });
    }

    const animate = () => {
      time += 0.01;
      
      // Clear canvas with fade effect
      ctx.fillStyle = 'rgba(255, 255, 255, 0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Draw black hole event horizon
      const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 150);
      gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
      gradient.addColorStop(0.3, 'rgba(0, 0, 0, 0.8)');
      gradient.addColorStop(0.6, 'rgba(50, 50, 50, 0.4)');
      gradient.addColorStop(0.8, 'rgba(100, 100, 100, 0.2)');
      gradient.addColorStop(1, 'rgba(150, 150, 150, 0.1)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 150, 0, Math.PI * 2);
      ctx.fill();
      
      // Draw accretion disk
      ctx.save();
      ctx.translate(centerX, centerY);
      ctx.rotate(time * 0.5);
      
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2;
        const radius = 180 + Math.sin(time * 2 + i) * 20;
        
        ctx.beginPath();
        ctx.strokeStyle = `rgba(${100 + i * 10}, ${100 + i * 10}, ${100 + i * 10}, ${0.3 - i * 0.03})`;
        ctx.lineWidth = 3;
        ctx.arc(0, 0, radius, angle, angle + Math.PI / 4);
        ctx.stroke();
      }
      
      ctx.restore();
      
      // Update and draw particles
      particles.forEach(particle => {
        // Calculate distance to center
        const dx = centerX - particle.x;
        const dy = centerY - particle.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // Apply gravitational pull
        if (distance > 50) {
          const force = 200 / (distance * distance);
          particle.vx += (dx / distance) * force;
          particle.vy += (dy / distance) * force;
          
          // Add orbital motion
          particle.vx += (-dy / distance) * force * 0.5;
          particle.vy += (dx / distance) * force * 0.5;
        }
        
        // Update position
        particle.x += particle.vx;
        particle.y += particle.vy;
        
        // Apply friction
        particle.vx *= 0.99;
        particle.vy *= 0.99;
        
        // Reset particles that get too close to center or too far
        if (distance < 50 || particle.x < 0 || particle.x > canvas.width || 
            particle.y < 0 || particle.y > canvas.height) {
          particle.x = Math.random() * canvas.width;
          particle.y = Math.random() * canvas.height;
          particle.vx = (Math.random() - 0.5) * 2;
          particle.vy = (Math.random() - 0.5) * 2;
        }
        
        // Draw particle
        ctx.save();
        ctx.globalAlpha = particle.opacity * (1 - distance / 500);
        ctx.fillStyle = `rgba(${Math.floor(255 - distance * 0.3)}, ${Math.floor(255 - distance * 0.3)}, ${Math.floor(255 - distance * 0.3)}, 1)`;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      });
      
      // Draw gravitational lensing effect
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const lensGradient = ctx.createRadialGradient(centerX, centerY, 140, centerX, centerY, 160);
      lensGradient.addColorStop(0, 'rgba(255, 255, 255, 0)');
      lensGradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.1)');
      lensGradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      ctx.fillStyle = lensGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 160, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => {
      window.removeEventListener('resize', resizeCanvas);
    };
  }, []);

  const handleEnterOrAi = () => {
    navigate('/core');
  };

  return (
    <div className="landing-page">
      <canvas ref={canvasRef} className="blackhole-canvas" />
      <div className="landing-content">
        <div className="top-content">
          <h1 className="landing-title">OrAi</h1>
          <p className="landing-subtitle">Where mysteries converge and reality bends</p>
        </div>
        <div className="void-space"></div>
        <div className="bottom-content">
          <button className="enter-void-btn" onClick={handleEnterOrAi}>
            ENTER THE VOID
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingPage; 